import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { useFeedback } from '@/components/notifications/Feedback/useFeedback';
import {
  getConnectionHealth,
  getConnectionHistory,
} from '@/graphql/connectionHealth';
import { useTrackUserEvent } from '@/hooks/useTrackUserEvent';
import { useDocumentDownload } from '@/modules/files/hooks/useFileDownload';
import { DocumentType, WorkflowTrackerStatus } from '@/types/schema';
import { getAppVersion } from '@/utils/environmentUtils';

import { NotificationContainer_WorkflowTrackerFragment } from '../graphql/CurrentUserWorkflowTrackersContext.generated';

// Stop waiting for an automatic download after 15 minutes; Activities retains its status.
const DOWNLOAD_WAIT_TIMEOUT_MS = 15 * 60_000;
// Capture a stalled wait once, without fetching data or attempting recovery.
const DOWNLOAD_DIAGNOSTIC_DELAY_MS = 60_000;

type ExportStage =
  | 'accepted'
  | 'still_waiting'
  | 'completion_observed'
  | 'output_unavailable'
  | 'generation_failed'
  | 'download_returned'
  | 'download_failed'
  | 'wait_expired';

interface PendingDownload {
  sourceDocumentId: string;
  startedAt: number;
  reportedWait: boolean;
  phase: 'awaiting_completion' | 'downloading';
  expiresAt: number;
}

// Keep this hook in the user-level provider so closing an export modal does
// not cancel its download. Only attempts started in this session are watched.
export function useWorkflowTrackerDownloads(
  userId: string | undefined,
  trackers: NotificationContainer_WorkflowTrackerFragment[]
) {
  const pending = useRef(new Map<string, PendingDownload>());
  // Diagnostics continue after the download leaves the generation wait queue.
  const observed = useRef(new Map<string, PendingDownload>());
  const [revision, setRevision] = useState(0);
  const session = useMemo(() => ({ userId }), [userId]);
  const activeSession = useRef<typeof session | undefined>(undefined);
  const [downloadDocument] = useDocumentDownload();
  const { showFeedback } = useFeedback();
  const trackUserEvent = useTrackUserEvent();
  const report = useCallback(
    (trackerId: string, request: PendingDownload, stage: ExportStage) => {
      if (activeSession.current !== session) return;
      if (
        stage !== 'accepted' &&
        stage !== 'completion_observed' &&
        stage !== 'still_waiting'
      ) {
        if (observed.current.get(trackerId) === request)
          observed.current.delete(trackerId);
      }
      try {
        trackUserEvent('document export diagnostic', {
          stage,
          phase: request.phase,
          workflow_tracker_id: trackerId,
          elapsed_ms: Date.now() - request.startedAt,
          frontend_release: getAppVersion(),
          ...getConnectionHealth(),
          // Successful milestones keep the small snapshot. Only stalled or
          // failed attempts upload the bounded transition history.
          ...(stage !== 'accepted' &&
          stage !== 'completion_observed' &&
          stage !== 'download_returned'
            ? { connection_history: getConnectionHistory() }
            : {}),
        });
      } catch {
        // Diagnostic failures must not interrupt an export.
      }
    },
    [session, trackUserEvent]
  );

  useEffect(() => {
    const requests = pending.current;
    const observations = observed.current;
    activeSession.current = session;
    return () => {
      activeSession.current = undefined;
      requests.clear();
      observations.clear();
    };
  }, [session]);

  const awaitTrackerDownload = useCallback(
    (trackerId: string, sourceDocumentId: string) => {
      // An enqueue response from a previous user session must not add work here.
      if (!session.userId || activeSession.current !== session) return;
      const request: PendingDownload = {
        sourceDocumentId,
        startedAt: Date.now(),
        reportedWait: false,
        phase: 'awaiting_completion',
        expiresAt: Date.now() + DOWNLOAD_WAIT_TIMEOUT_MS,
      };
      pending.current.set(trackerId, request);
      observed.current.set(trackerId, request);
      report(trackerId, request, 'accepted');
      setRevision((value) => value + 1);
    },
    [session, report]
  );

  useEffect(() => {
    if (!session.userId || pending.current.size === 0) return;
    for (const tracker of trackers) {
      const request = pending.current.get(tracker.id);
      if (!request) continue;
      if (
        tracker.status !== WorkflowTrackerStatus.Completed &&
        tracker.status !== WorkflowTrackerStatus.Failed
      ) {
        continue;
      }

      // Remove synchronously before downloading: repeated tracker updates must
      // not start the same download while the first one is still running.
      pending.current.delete(tracker.id);
      if (tracker.status === WorkflowTrackerStatus.Failed) {
        report(tracker.id, request, 'generation_failed');
        showFeedback('The document summary export failed. Please try again.');
        continue;
      }

      report(tracker.id, request, 'completion_observed');
      const outputs = tracker.documents?.filter(
        (document) =>
          document.id !== request.sourceDocumentId &&
          document.type === DocumentType.GeneratedDocument
      );
      const output = outputs?.[0];
      if (!output || outputs.length !== 1) {
        report(tracker.id, request, 'output_unavailable');
        showFeedback(
          'The export completed, but its download is unavailable. Please try again.'
        );
        continue;
      }

      // useDocumentDownload reports failures and shows download feedback.
      // A resolved call does not prove that the browser saved the file.
      request.phase = 'downloading';
      void downloadDocument(output.id).then(
        () => report(tracker.id, request, 'download_returned'),
        () => report(tracker.id, request, 'download_failed')
      );
    }

    if (pending.current.size === 0) return;
    const nextExpiry = Math.min(
      ...Array.from(pending.current.values(), (request) => request.expiresAt)
    );
    const timeout = setTimeout(
      () => {
        for (const [id, request] of pending.current) {
          if (request.expiresAt <= Date.now()) {
            report(id, request, 'wait_expired');
            pending.current.delete(id);
          }
        }
        setRevision((value) => value + 1);
        showFeedback(
          'The export is taking longer than expected. Check Activities for its status.'
        );
      },
      Math.max(0, nextExpiry - Date.now())
    );
    return () => clearTimeout(timeout);
  }, [session, trackers, revision, downloadDocument, showFeedback, report]);

  useEffect(() => {
    const unreported = Array.from(observed.current.values()).filter(
      (request) => !request.reportedWait
    );
    if (!unreported.length) return;
    const nextReportAt = Math.min(
      ...unreported.map(
        (request) => request.startedAt + DOWNLOAD_DIAGNOSTIC_DELAY_MS
      )
    );
    const timeout = setTimeout(
      () => {
        for (const [id, request] of observed.current) {
          if (
            !request.reportedWait &&
            Date.now() >= request.startedAt + DOWNLOAD_DIAGNOSTIC_DELAY_MS
          ) {
            request.reportedWait = true;
            report(id, request, 'still_waiting');
          }
        }
        setRevision((value) => value + 1);
      },
      Math.max(0, nextReportAt - Date.now())
    );
    return () => clearTimeout(timeout);
  }, [revision, session, report]);

  return awaitTrackerDownload;
}
